import Downshift from 'downshift'
import { useEffect, useRef, useState } from 'react'

const CustomSelect = props => {
  const {
    options,
    onInputChange,
    filterOptions,
    optionKey,
    itemToString,
    renderOption,
    clearSelection: clearModelSelection,
    className,
    optionFromInput,
    selectedItem,
    initialSelectedItem,
    initialSelectedItemUpdatesInput = false,
    /*
     *When you are allowed to provide freeform input, this allows us to autoselect
     *the option that is created when no selection is made.  You must provide
     * `optionFromInput` for this to work
     */
    selectInputOnBlur
  } = props
  const [inputValue, setInputValue] = useState(itemToString(initialSelectedItem) ?? '')
  const inputRef = useRef(null)

  useEffect(() => {
    setInputValue(itemToString(selectedItem) ?? '')
  }, [selectedItem])

  useEffect(() => {
    if (initialSelectedItemUpdatesInput) {
      setInputValue(itemToString(initialSelectedItem) ?? '')
    }
  }, [initialSelectedItemUpdatesInput, itemToString, initialSelectedItem])

  return (
    <Downshift
      inputValue={inputValue}
      onSelect={item => {
        /*
         * Only if item is defined, otherwise this will clear the input
         * when a selected item is edited (thereby unselecting it)
         */
        if (item) {
          setInputValue(itemToString(item))
        }
      }}
      {...props}>
      {({
        getInputProps,
        getItemProps,
        getMenuProps,
        isOpen,
        highlightedIndex,
        selectItem,
        selectedItem,
        getRootProps,
        openMenu,
        clearSelection
      }) => {
        //debugger;
        const identity = item => item
        let filterOptionsFunc = filterOptions

        /*
         * Pass `true` to filter based on whether the options include the input text
         */
        if (filterOptions === true) {
          filterOptionsFunc = item =>
            !inputValue || itemToString(item).includes(inputValue)
        }

        filterOptionsFunc = filterOptionsFunc ?? identity
        let filteredOptions = options.filter(filterOptionsFunc)
        const dynamicOption = optionFromInput?.(inputValue)

        if (
          dynamicOption &&
          //Only include the dynamicOption if it doesn't already exist
          !filteredOptions.find(
            option => itemToString(option) === itemToString(dynamicOption)
          )
        ) {
          filteredOptions = [...filteredOptions, dynamicOption]
        }

        const renderOptionFunc =
          renderOption ?? (item => <span>{itemToString(item)}</span>)
        const optionKeyFunc = optionKey ?? (item => itemToString(item))
        /*
         * Ensure that the return of onInputChange (if there is one) is a promise.
         * This way, when the promise completes we can check it to see if it's an
         * array with elements and potentially open the menu
         */
        const wrappedOnInputChange = inputValue => {
          //console.log("Calling wrapped func", onInputChange)
          return Promise.resolve(onInputChange?.(inputValue))
        }

        //console.log("selectedItem", selectedItem, renderSelectionFunc(selectedItem));
        return (
          <div>
            <div
              className={selectedItem && clearSelection ? 'input-group' : ''}
              {...getRootProps({}, { suppressRefError: true })}>
              <input
                {...getInputProps({
                  className,
                  value: inputValue,
                  ref: inputRef,
                  onChange: e => {
                    if (selectedItem) {
                      clearSelection()
                    }

                    const inputValue = e.target.value

                    setInputValue(inputValue)
                    wrappedOnInputChange(inputValue)
                  },
                  onFocus: e => {
                    /*
                     * Sometimes we have the search results immediately available
                     * like in the school search for customers.  This loads those
                     * results when the input is focused and opens the menu if
                     * the options exist
                     */
                    wrappedOnInputChange(inputValue).then(resp => {
                      //console.log("Focused with search results of", resp);
                      if (resp?.length) {
                        openMenu()
                      }
                    })
                  },
                  onBlur: e => {
                    /*
                     * If no item was selected before blur,
                     * 1. check to see if we should "select on blur", and try to do that if we should
                     * 2. clear the selection if no selection can be derived
                     */
                    if (!selectedItem) {
                      let selection = null

                      if (selectInputOnBlur) {
                        selection = optionFromInput?.(inputValue)
                      }

                      selectItem(selection)

                      //Above won't clear the input if selection is empty
                      if (!selection) {
                        setInputValue('')
                      }
                    }
                  }
                })}
              />
              {clearSelection && selectedItem ? (
                <span className="input-group-addon">
                  <a
                    aria-label="Remove Selection"
                    role="button"
                    onClick={() => {
                      clearModelSelection()
                      clearSelection()
                      setInputValue('')
                      inputRef?.current?.focus?.()
                      wrappedOnInputChange('').then(resp => {
                        if (resp?.length) {
                          openMenu()
                        }
                      })
                    }}>
                    <i className="times" />
                  </a>
                </span>
              ) : null}
            </div>
            {isOpen ? (
              <ul
                {...getMenuProps()}
                style={{
                  maxHeight: '350px',
                  overflow: 'scroll',
                  position: 'absolute',
                  width: 'calc(100% - 30px)'
                }}
                className="headed2-dropdown-menu">
                {filteredOptions.map((item, index) => (
                  <li
                    className="dropdown-menu-item"
                    {...getItemProps({
                      key: optionKeyFunc(item),
                      index,
                      item,
                      style: {
                        backgroundColor:
                          highlightedIndex === index ? 'lightgray' : 'white',
                        fontWeight: selectedItem === item ? 'bold' : 'normal'
                      }
                    })}>
                    {renderOptionFunc(item)}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )
      }}
    </Downshift>
  )
}

export default CustomSelect
